import { validateParticipantsForMethod } from 'modules/calendar/utils/validateParticipantsForMethod';
import * as Yup from 'yup';
import { AvailabilityMethod } from 'generated-graphql/graphql';
import { availabilitySchema } from './apiSchemas';
import { usePostRest } from 'common/hooks';
import {
  positiveNumberSchema,
  nonNegativeNumberSchema
} from 'common/form/validationSchema/number';
import { ApiPaths } from '../constants/apiPaths';

export type APIMeetingAvailability = Yup.InferType<typeof availabilitySchema>;

export const apiCreateCalendarMeetingTemplateSchema = Yup.object({
  title: Yup.string().min(1, 'Enter meeting title.').required(),
  description: Yup.string().optional(),
  videoConference: Yup.boolean().default(false).required(),
  durationMinutes: positiveNumberSchema
    .required()
    .max(1440, 'Duration must be at most 1440 minutes (24 hours)'),
  participants: Yup.array(
    Yup.object({
      email: Yup.string().email('Invalid email format').required(),
      name: Yup.string().min(1, 'Name is required').required(),
      isOrganizer: Yup.boolean().required(),
      autoCollectAvailability: Yup.boolean().optional()
    })
  )
    .required()
    .test('participants-for-method', function (value) {
      const error = validateParticipantsForMethod(
        this.parent.availabilityMethod,
        value
      );

      return error ? this.createError({ message: error }) : true;
    }),
  availability: availabilitySchema.required(),
  timezone: Yup.string()
    .test(
      'valid-timezone',
      'Must be valid IANA timezone identifier',
      (timeZone) => {
        try {
          Intl.DateTimeFormat(undefined, { timeZone });
          return true;
        } catch {
          return false;
        }
      }
    )
    .required(),
  sendMeetingReminder: positiveNumberSchema.required(),
  buffer: nonNegativeNumberSchema
    .max(120)
    .test(
      'buffer-multiple-of-5',
      'Buffer must be multiple of 5 minutes',
      function (value) {
        return (value ?? 0) % 5 === 0;
      }
    )
    .required(),
  // calendar-service accepts 0 (no notice required), and the form allows it.
  noticePeriodMinutes: nonNegativeNumberSchema.required(),
  availableDaysInTheFuture: positiveNumberSchema.required(),
  location: Yup.string().optional(),
  availabilityMethod: Yup.mixed<AvailabilityMethod>()
    .oneOf(Object.values(AvailabilityMethod))
    .optional(),
  // Without .default(undefined) yup materialises {} and fails its own inner required.
  availabilityOutreachSettings: Yup.object({
    requiredHours: positiveNumberSchema.min(1).max(168).required()
  })
    .default(undefined)
    .optional()
}).required();

export type APICalendarMeetingTemplateSchema = Yup.InferType<
  typeof apiCreateCalendarMeetingTemplateSchema
>;

export const useCreateCalendarMeetingTemplate = () =>
  usePostRest<{ data: { id: string } }, APICalendarMeetingTemplateSchema>(
    ApiPaths.CREATE_CALENDAR_MEETING_TEMPLATE
  );
